import { HttpErrorResponse, HttpRequest, HttpResponse } from '@angular/common/http';

import { Observable, delay, of, switchMap, throwError, timer } from 'rxjs';

import type {
  BookingRefunds,
  ManualRefund,
  ManualRefundCreate,
  ManualRefundStatus,
  ManualRefundTransition,
} from '@interfaces/refund.interface';
import { refundConsumesBalance, refundTransitionsFrom } from '@interfaces/refund.interface';

const MOCK_DELAY = 300;
const MOCK_TOTAL_PAID = 450;
const MOCK_CURRENCY = 'EUR';

const registries = new Map<string, BookingRefunds>();
const idempotency = new Map<string, string>();
let sequence = 0;

function registryOf(bookingId: string): BookingRefunds {
  let registry = registries.get(bookingId);
  if (!registry) {
    registry = {
      bookingId,
      currency: MOCK_CURRENCY,
      totalPaid: MOCK_TOTAL_PAID,
      totalReserved: 0,
      remainingBalance: MOCK_TOTAL_PAID,
      canRecord: true,
      requiresLegacyReconciliation: false,
      blockingReason: null,
      refunds: [],
    };
    registries.set(bookingId, registry);
  }
  return registry;
}

function reservedOf(registry: BookingRefunds): number {
  return registry.refunds
    .filter(refund => refundConsumesBalance(refund.status))
    .reduce((sum, refund) => sum + refund.amount, 0);
}

function ok<T>(body: T, status = 200): Observable<HttpResponse<T>> {
  return of(new HttpResponse({ status, body })).pipe(delay(MOCK_DELAY));
}

function codedError(status: number, code: string): Observable<never> {
  return timer(MOCK_DELAY).pipe(
    switchMap(() =>
      throwError(
        () =>
          new HttpErrorResponse({
            status,
            statusText: code,
            error: {
              errorCode: code,
              message: `mocked ${code}`,
              correlationId: crypto.randomUUID(),
              details: {},
            },
          })
      )
    )
  );
}

/**
 * Offline demonstration of the implemented manual-refunds endpoints: keeps a per-booking
 * registry in memory, enforces the balance and the canonical state chain, and honours the
 * Idempotency-Key so a retried create returns the already-recorded refund.
 */
export function handleRefundsMock(
  req: HttpRequest<unknown>
): Observable<HttpResponse<unknown>> | null {
  const match = /\/bookings\/([^/]+)\/refunds(?:\/([^/]+)\/status)?$/.exec(req.url);
  if (!match) {
    return null;
  }
  const [, bookingId, refundId] = match;
  const registry = registryOf(bookingId);

  if (req.method === 'GET' && !refundId) {
    return ok<BookingRefunds>({
      ...registry,
      totalReserved: reservedOf(registry),
      remainingBalance: Math.round((registry.totalPaid - reservedOf(registry)) * 100) / 100,
      canRecord: reservedOf(registry) < registry.totalPaid,
      refunds: [...registry.refunds],
    });
  }

  if (req.method === 'POST' && !refundId) {
    const key = req.headers.get('Idempotency-Key') ?? '';
    const body = req.body as ManualRefundCreate;
    const existingId = key ? idempotency.get(key) : undefined;
    if (existingId) {
      const existing = registry.refunds.find(refund => refund.id === existingId);
      if (existing) {
        return ok(existing);
      }
    }
    if (!body || typeof body.amount !== 'number' || body.amount <= 0) {
      return codedError(422, 'REFUND_AMOUNT_INVALID');
    }
    if (!body.externalReference?.trim() || !body.note?.trim()) {
      return codedError(422, 'REFUND_FIELDS_REQUIRED');
    }
    if (body.amount > registry.totalPaid - reservedOf(registry)) {
      return codedError(422, 'REFUND_AMOUNT_EXCEEDS_BALANCE');
    }
    sequence += 1;
    const now = new Date().toISOString();
    const refund: ManualRefund = {
      id: `mock-refund-${sequence}`,
      amount: body.amount,
      currency: registry.currency,
      status: 'RECORDED',
      externalReference: body.externalReference.trim(),
      note: body.note.trim(),
      actor: 'admin@mojacardream.com',
      createdAt: now,
      updatedAt: now,
    };
    registry.refunds.push(refund);
    if (key) {
      idempotency.set(key, refund.id);
    }
    return ok(refund, 200);
  }

  if (req.method === 'POST' && refundId) {
    const { status, reason } = req.body as { status: ManualRefundTransition; reason?: string };
    const refund = registry.refunds.find(entry => entry.id === refundId);
    if (!refund) {
      return codedError(404, 'REFUND_NOT_FOUND');
    }
    if (!refundTransitionsFrom(refund.status).includes(status)) {
      return codedError(409, 'REFUND_INVALID_TRANSITION');
    }
    if (status === 'REJECTED' && !reason?.trim()) {
      return codedError(422, 'REFUND_REJECT_REASON_REQUIRED');
    }
    const updated: ManualRefund = {
      ...refund,
      status: status as ManualRefundStatus,
      updatedAt: new Date().toISOString(),
      rejectedReason: status === 'REJECTED' ? (reason?.trim() ?? null) : refund.rejectedReason,
    };
    registry.refunds.splice(registry.refunds.indexOf(refund), 1, updated);
    return ok(updated);
  }

  return null;
}
